/**
 * Mural onboarding animation — ported from the prototype at
 * inspirations/animation/mural-scenes.jsx. The scene code below mirrors the
 * prototype's structure 1:1 (same names, timings, easings, copy) so future
 * edits map back to it. The prototype's Stage/SceneStage editor engine is
 * replaced by a minimal requestAnimationFrame player (see MuralOnboardingAnimation).
 * Unlike the prototype's continuous timeline, the player pauses on the last
 * frame of scenes flagged `pauseAfter` and waits for a click ("Continuar")
 * before moving on; the remaining scenes flow into each other uninterrupted.
 *
 * Design canvas: 1920x1080 (the prototype root mounts
 * `<SceneStage width={1920} height={1080}>`; all scene coordinates target it),
 * auto-scaled to fit the mount container.
 */
import React, { useEffect, useRef, useState } from 'react';

import { AnnouncementAnimationProps } from './types';

/* ---------- Palette / fonts / config (prototype: C, OSW, SERIF, CFG) ---------- */
const C = {
  red: '#D41920',
  red2: '#E84B5A',
  pink: '#F2A0C4',
  pale: '#F8D7DD',
  ink: '#1A1018',
  cream: '#FFF8F5',
} as const;
const OSW = "'Oswald', sans-serif";
const SERIF = "'DM Serif Text', serif";
// Bilingual captions always on (pt headline + English subtitle) — matches the
// product (English school for Portuguese speakers) and the prototype default.
const SHOW_ENGLISH = true;

/* ---------- Easing helpers (prototype: seg, lerp, eo, eio, backOut) ---------- */
const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
const seg = (t: number, a: number, b: number) => clamp((t - a) / (b - a), 0, 1);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const eo = (t: number) => 1 - Math.pow(1 - t, 3);
const eio = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const backOut = (t: number) => {
  const c = 2.3;
  const u = t - 1;
  return 1 + u * u * ((c + 1) * u + c);
};

/* Placeholder screenshots — the team will replace these 1:1 by filename. */
const PHOTOS = [
  '/animations/mural/photo-1.png',
  '/animations/mural/photo-2.png',
  '/animations/mural/photo-3.png',
  '/animations/mural/photo-4.png',
];

const GRAIN =
  'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27200%27 height=%27200%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27/%3E%3C/filter%3E%3Crect width=%27200%27 height=%27200%27 filter=%27url(%23n)%27 opacity=%270.5%27/%3E%3C/svg%3E")';

// `portrait` is not in the prototype: the player sets it on tall (mobile)
// containers so a few scenes can narrow captions that are wider than the
// portrait crop window. Scenes ignore it on desktop (undefined/false).
type SceneProps = { t: number; dur: number; portrait?: boolean };

/* ---------- Shared presentational pieces (prototype: Cap, Tape, Wall, ...) ---------- */
function Cap({
  pt,
  en,
  t,
  t0,
  color = C.ink,
  size = 66,
  style,
}: {
  pt: string;
  en?: string;
  t: number;
  t0: number;
  color?: string;
  size?: number;
  style?: React.CSSProperties;
}) {
  const p = eo(seg(t, t0, t0 + 0.6));
  return (
    <div style={{ opacity: p, transform: `translateY(${lerp(26, 0, p)}px)`, ...style }}>
      <div
        style={{
          fontFamily: OSW,
          fontWeight: 600,
          fontSize: size,
          lineHeight: 1.04,
          letterSpacing: '0.015em',
          textTransform: 'uppercase',
          color,
          textWrap: 'balance',
        }}
      >
        {pt}
      </div>
      {SHOW_ENGLISH && en && (
        <div
          style={{
            fontFamily: SERIF,
            fontStyle: 'italic',
            fontSize: Math.round(size * 0.4),
            marginTop: 12,
            color,
            opacity: 0.72,
          }}
        >
          {en}
        </div>
      )}
    </div>
  );
}

const Tape = ({ x, y, rot = 0, w = 130 }: { x: number; y: number; rot?: number; w?: number }) => (
  <div
    style={{
      position: 'absolute',
      left: x,
      top: y,
      width: w,
      height: 36,
      transform: `rotate(${rot}deg)`,
      background: 'rgba(248,215,221,0.88)',
      boxShadow: '0 1px 4px rgba(26,16,24,0.25)',
    }}
  />
);

function Wall({ children, wash = 0.35 }: { children?: React.ReactNode; wash?: number }) {
  return (
    <div style={{ position: 'absolute', inset: 0, background: '#EFE3DA', overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'repeating-linear-gradient(0deg, rgba(26,16,24,0.07) 0 3px, transparent 3px 72px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(112deg, rgba(255,248,245,${wash + 0.25}) 0%, rgba(255,248,245,${wash}) 45%, rgba(255,248,245,${wash + 0.35}) 100%)`,
        }}
      />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: GRAIN, opacity: 0.1 }} />
      {children}
    </div>
  );
}

const EmptyFrame = ({
  x,
  y,
  w = 300,
  h = 400,
  rot = 0,
  op = 1,
}: {
  x: number;
  y: number;
  w?: number;
  h?: number;
  rot?: number;
  op?: number;
}) => (
  <div
    style={{
      position: 'absolute',
      left: x,
      top: y,
      width: w,
      height: h,
      transform: `rotate(${rot}deg)`,
      border: '3px solid rgba(26,16,24,0.16)',
      background: 'rgba(255,248,245,0.35)',
      opacity: op,
    }}
  />
);

function SeloSVG({ size = 250, word = 'CORAGEM' }: { size?: number; word?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" style={{ display: 'block' }}>
      <defs>
        <path id="seloArc" d="M100,100 m-70,0 a70,70 0 1,1 140,0 a70,70 0 1,1 -140,0" />
      </defs>
      <circle cx="100" cy="100" r="97" fill={C.red} />
      <circle
        cx="100"
        cy="100"
        r="83"
        fill="none"
        stroke={C.cream}
        strokeWidth="2.5"
        strokeDasharray="5 7"
      />
      <text
        fontFamily="Oswald, sans-serif"
        fontSize="22"
        fontWeight="600"
        letterSpacing="4"
        fill={C.cream}
      >
        <textPath href="#seloArc">
          ✦ {word} ✦ {word}
        </textPath>
      </text>
      <text
        x="100"
        y="122"
        textAnchor="middle"
        fontSize="60"
        fill={C.cream}
        fontFamily="Oswald, sans-serif"
        fontWeight="700"
      >
        ✦
      </text>
    </svg>
  );
}

function Cartaz({
  w = 440,
  revealed = false,
  rot = -2,
  style,
}: {
  w?: number;
  revealed?: boolean;
  rot?: number;
  style?: React.CSSProperties;
}) {
  const h = w * 1.36;
  const pad = w * 0.075;
  return (
    <div
      style={{
        position: 'relative',
        width: w,
        height: h,
        transform: `rotate(${rot}deg)`,
        background: revealed ? C.pale : '#F4EAE3',
        boxShadow: '0 18px 50px rgba(26,16,24,0.28)',
        clipPath: 'polygon(0 0, 100% 0, 100% 94%, 92% 100%, 0 100%)',
        ...style,
      }}
    >
      <div style={{ position: 'absolute', inset: 0, backgroundImage: GRAIN, opacity: 0.07 }} />
      <div
        style={{ position: 'absolute', inset: 0, padding: pad, display: 'flex', flexDirection: 'column' }}
      >
        <div
          style={{
            fontFamily: OSW,
            fontWeight: 500,
            fontSize: w * 0.045,
            letterSpacing: '0.22em',
            color: C.red,
            textTransform: 'uppercase',
          }}
        >
          Unidade 12 · Ativista
        </div>
        <div style={{ marginTop: w * 0.06, flex: 1 }}>
          {revealed ? (
            <div
              style={{
                fontFamily: SERIF,
                fontStyle: 'italic',
                fontSize: w * 0.078,
                lineHeight: 1.25,
                color: C.ink,
              }}
            >
              “Quero ser lembrada como uma pessoa que queria ser livre.”
            </div>
          ) : (
            <div>
              <div
                style={{
                  width: '88%',
                  height: w * 0.055,
                  background: 'rgba(26,16,24,0.12)',
                  marginBottom: w * 0.035,
                }}
              />
              <div
                style={{
                  width: '72%',
                  height: w * 0.055,
                  background: 'rgba(26,16,24,0.12)',
                  marginBottom: w * 0.035,
                }}
              />
              <div style={{ width: '80%', height: w * 0.055, background: 'rgba(26,16,24,0.12)' }} />
            </div>
          )}
        </div>
        {revealed ? (
          <div
            style={{
              fontFamily: OSW,
              fontWeight: 700,
              fontSize: w * 0.155,
              lineHeight: 0.98,
              textTransform: 'uppercase',
              color: C.ink,
            }}
          >
            Rosa
            <br />
            Parks
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div
              style={{
                fontFamily: OSW,
                fontWeight: 700,
                fontSize: w * 0.155,
                lineHeight: 0.98,
                textTransform: 'uppercase',
                color: 'rgba(26,16,24,0.14)',
              }}
            >
              ████
              <br />
              █████
            </div>
            <div
              style={{
                position: 'absolute',
                left: -w * 0.02,
                top: w * 0.09,
                transform: 'rotate(-4deg)',
                background: C.cream,
                padding: `${w * 0.028}px ${w * 0.055}px`,
                boxShadow: '0 4px 14px rgba(26,16,24,0.25)',
                clipPath: 'polygon(1% 8%, 99% 0, 100% 88%, 97% 100%, 2% 96%, 0 82%)',
              }}
            >
              <div
                style={{
                  fontFamily: OSW,
                  fontWeight: 700,
                  fontSize: w * 0.095,
                  textTransform: 'uppercase',
                  color: C.red,
                  letterSpacing: '0.08em',
                }}
              >
                Quem será?
              </div>
            </div>
          </div>
        )}
        <div
          style={{
            marginTop: w * 0.045,
            fontFamily: OSW,
            fontWeight: 500,
            fontSize: w * 0.033,
            letterSpacing: '0.08em',
            whiteSpace: 'nowrap',
            color: C.ink,
            opacity: 0.65,
          }}
        >
          1913 – 2005 · ESTADOS UNIDOS
        </div>
      </div>
      <Tape x={w * 0.32} y={-16} rot={-4} w={w * 0.32} />
    </div>
  );
}

/* ---------- Scene 1: Montage ---------- */
const SHOTS = [
  // enter from: left, right, top, bottom — settle overlapping, filling the canvas
  { src: PHOTOS[0], x: -80, y: -60, w: 980, rot: -2.5, from: [-1400, 0], t0: 0.15 },
  { src: PHOTOS[1], x: 1060, y: -80, w: 880, rot: 2, from: [1400, 0], t0: 1.0 },
  { src: PHOTOS[2], x: 330, y: 100, w: 640, rot: -1.5, from: [0, -1300], t0: 1.85 },
  { src: PHOTOS[3], x: 880, y: 300, w: 900, rot: 1.5, from: [300, 1300], t0: 2.7 },
];
function Montage({ t, dur, portrait }: SceneProps) {
  const capT = eo(seg(t, 3.9, 4.6));
  const drift = lerp(1, 1.035, seg(t, 0, dur));
  return (
    <div style={{ position: 'absolute', inset: 0, background: C.ink, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, transform: `scale(${drift})` }}>
        {SHOTS.map((sh, i) => {
          const p = eo(seg(t, sh.t0, sh.t0 + 0.85));
          if (p === 0) return null;
          const tx = lerp(sh.from[0], 0, p);
          const ty = lerp(sh.from[1], 0, p);
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: sh.x,
                top: sh.y,
                width: sh.w,
                transform: `translate(${tx}px, ${ty}px) rotate(${lerp(sh.rot * 3, sh.rot, p)}deg)`,
                background: C.cream,
                padding: 16,
                boxShadow: '0 24px 60px rgba(26,16,24,0.55)',
              }}
            >
              <img
                src={sh.src}
                alt=""
                style={{
                  display: 'block',
                  width: '100%',
                  filter: 'grayscale(1) contrast(1.12) brightness(0.95)',
                }}
              />
            </div>
          );
        })}
      </div>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: GRAIN, opacity: 0.12 }} />
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          // portrait-only: narrow left column (the crop window ends at x~730)
          // and extra bottom clearance so the pause scraps don't cover the text.
          padding: portrait ? '120px 60px 200px' : '120px 100px 80px',
          background: 'linear-gradient(to top, rgba(26,16,24,0.88) 30%, transparent)',
          opacity: capT,
        }}
      >
        <div
          style={{ maxWidth: portrait ? 620 : 1350, transform: `translateY(${lerp(26, 0, capT)}px)` }}
        >
          <div
            style={{ display: 'inline-block', background: C.red, padding: '10px 26px', marginBottom: 18 }}
          >
            <span
              style={{
                fontFamily: OSW,
                fontWeight: 600,
                fontSize: 30,
                letterSpacing: '0.25em',
                color: C.cream,
                textTransform: 'uppercase',
              }}
            >
              Há mais de um século
            </span>
          </div>
          <div
            style={{
              fontFamily: OSW,
              fontWeight: 700,
              fontSize: 80,
              lineHeight: 1.0,
              textTransform: 'uppercase',
              color: C.cream,
              textWrap: 'balance',
            }}
          >
            Mulheres ocupam as ruas com cartazes.
          </div>
          {SHOW_ENGLISH && (
            <div
              style={{
                fontFamily: SERIF,
                fontStyle: 'italic',
                fontSize: 32,
                marginTop: 14,
                color: C.cream,
                opacity: 0.75,
              }}
            >
              For over a century, women have taken the streets with posters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Scene 2: The empty wall ---------- */
function WallScene({ t, portrait }: SceneProps) {
  const frames = [
    { x: 130, y: 140, rot: -2.5, t0: 0.25 },
    { x: 520, y: 420, rot: 1.5, t0: 0.45 },
    { x: 1450, y: 180, rot: 2, t0: 0.65 },
    { x: 1180, y: 560, rot: -1.5, t0: 0.85 },
  ];
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <Wall>
        {frames.map((f, i) => (
          <EmptyFrame key={i} x={f.x} y={f.y} rot={f.rot} op={eo(seg(t, f.t0, f.t0 + 0.5))} />
        ))}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* portrait-only: tighter size/width so the centered caption wraps
              inside the ~700px-wide crop window instead of being cut. */}
          <Cap
            pt="Este é o seu mural."
            en="This is your wall. Today, it's empty."
            t={t}
            t0={1.3}
            size={portrait ? 92 : 110}
            style={{ textAlign: 'center', maxWidth: portrait ? 620 : 1200 }}
          />
        </div>
      </Wall>
    </div>
  );
}

/* ---------- Scene 3: Teaser cartaz ---------- */
function Teaser({ t }: SceneProps) {
  const p = eo(seg(t, 0.15, 0.8));
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <Wall>
        <EmptyFrame x={1490} y={150} rot={2} />
        <EmptyFrame x={1560} y={640} rot={-1.5} w={260} h={340} />
        <div
          style={{
            position: 'absolute',
            left: 170,
            top: 130,
            opacity: p,
            transform: `translateY(${lerp(40, 0, p)}px)`,
          }}
        >
          <Cartaz w={460} revealed={false} />
        </div>
        <div style={{ position: 'absolute', left: 780, top: 340, maxWidth: 660 }}>
          <Cap
            pt="Cada unidade honra uma mulher."
            en="Each unit honors a woman — finish it to meet her."
            t={t}
            t0={1.1}
            size={78}
          />
        </div>
      </Wall>
    </div>
  );
}

/* ---------- Scene 4: Classes + EP ---------- */
function Classes({ t, portrait }: SceneProps) {
  const rows = [
    { label: 'Aula 1', ep: '+10 EP', t0: 0.9 },
    { label: 'Aula 2', ep: '+10 EP', t0: 1.8 },
    { label: 'Aula 3', ep: '+10 EP', t0: 2.7 },
    { label: 'Aula 4', ep: '+20 EP', t0: 3.6 },
  ];
  const done = rows.filter((r) => t >= r.t0).length;
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <Wall>
        <div style={{ position: 'absolute', left: 150, top: 120 }}>
          <Cartaz w={380} revealed={false} />
          <div style={{ display: 'flex', gap: 10, marginTop: 34, transform: 'rotate(-2deg)' }}>
            {rows.map((r, i) => (
              <div
                key={i}
                style={{
                  width: 84,
                  height: 20,
                  background: i < done ? C.red : 'rgba(26,16,24,0.12)',
                  transition: 'background 0.25s',
                }}
              />
            ))}
          </div>
        </div>
        {/* portrait-only width: keep the caption no wider than the rows (780)
            so the crop window over the right column shows it whole. */}
        <div style={{ position: 'absolute', left: 720, top: 110, width: portrait ? 780 : 1030 }}>
          <Cap
            pt="A cada aula, você ganha pontos de empoderamento — e, ao final da unidade, um cartaz."
            en="Each class earns you Empowerment Points — and at the end of the unit, her poster."
            t={t}
            t0={0.15}
            size={60}
          />
          <div style={{ marginTop: 46, display: 'flex', flexDirection: 'column', gap: 18 }}>
            {rows.map((r, i) => {
              const rp = seg(t, r.t0, r.t0 + 0.4);
              const pop = backOut(seg(t, r.t0, r.t0 + 0.5));
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 22,
                    background: 'rgba(255,248,245,0.85)',
                    border: '2px solid rgba(26,16,24,0.12)',
                    padding: '20px 28px',
                    width: 780,
                  }}
                >
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      border: `3px solid ${rp > 0 ? C.red : 'rgba(26,16,24,0.25)'}`,
                      background: rp > 0.5 ? C.red : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {rp > 0.5 && (
                      <span style={{ color: C.cream, fontFamily: OSW, fontWeight: 700, fontSize: 24 }}>
                        ✓
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontFamily: OSW,
                      fontWeight: 600,
                      fontSize: 34,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color: C.ink,
                      flex: 1,
                    }}
                  >
                    {r.label}
                  </div>
                  <div
                    style={{
                      opacity: seg(t, r.t0, r.t0 + 0.3),
                      transform: `scale(${Math.max(pop, 0.01)})`,
                      background: C.pale,
                      border: `2px solid ${C.red2}`,
                      color: C.red,
                      fontFamily: OSW,
                      fontWeight: 700,
                      fontSize: 28,
                      padding: '6px 18px',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {r.ep}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Wall>
    </div>
  );
}

/* ---------- Scene 5: Ceremony ---------- */
function Ceremony({ t }: SceneProps) {
  const lift = eo(seg(t, 0, 0.9));
  const stampP = seg(t, 1.5, 1.85);
  const pulse = seg(t, 1.85, 2.7);
  const l1 = eo(seg(t, 2.5, 3.0));
  const l2 = eo(seg(t, 3.2, 3.7));
  const l3 = eo(seg(t, 3.9, 4.4));
  return (
    <div style={{ position: 'absolute', inset: 0, background: C.ink, overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 60% 75% at 32% 46%, rgba(248,215,221,0.14), transparent 70%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: 250,
          top: 120,
          transform: `translateY(${lerp(70, 0, lift)}px) scale(${lerp(0.94, 1, lift)})`,
          opacity: lift,
        }}
      >
        <Cartaz w={480} revealed={true} rot={-2} />
        {stampP > 0 && (
          <div
            style={{
              position: 'absolute',
              right: -70,
              bottom: -60,
              transform: `rotate(-14deg) scale(${lerp(2.6, 1, backOut(stampP))})`,
              opacity: seg(stampP, 0, 0.4),
            }}
          >
            <SeloSVG size={240} word="CORAGEM" />
            {pulse > 0 && pulse < 1 && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  border: `4px solid ${C.red2}`,
                  transform: `scale(${lerp(1, 1.7, eo(pulse))})`,
                  opacity: 1 - pulse,
                }}
              />
            )}
          </div>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          left: 900,
          top: 300,
          width: 900,
          display: 'flex',
          flexDirection: 'column',
          gap: 30,
        }}
      >
        <div
          style={{
            opacity: l1,
            transform: `translateY(${lerp(24, 0, l1)}px)`,
            fontFamily: OSW,
            fontWeight: 700,
            fontSize: 74,
            textTransform: 'uppercase',
            lineHeight: 1.02,
            color: C.cream,
          }}
        >
          Cartaz colado
          <br />
          no seu mural
        </div>
        <div
          style={{
            opacity: l2,
            transform: `translateY(${lerp(24, 0, l2)}px)`,
            fontFamily: OSW,
            fontWeight: 600,
            fontSize: 44,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: C.pink,
          }}
        >
          Você ganhou o selo da Coragem ✦
        </div>
        <div
          style={{
            opacity: l3,
            transform: `translateY(${lerp(24, 0, l3)}px)`,
            fontFamily: SERIF,
            fontStyle: 'italic',
            fontSize: 40,
            color: C.cream,
          }}
        >
          de Rosa Parks para você
        </div>
      </div>
    </div>
  );
}

/* ---------- Scene 6: Zoom out to the whole mural ---------- */
const LEVELS = [
  { name: 'C2', state: 'future' },
  { name: 'C1', state: 'future' },
  { name: 'B2', state: 'next' },
  { name: 'B1', state: 'current' },
  { name: 'A2', state: 'done' },
  { name: 'A1', state: 'done' },
  { name: 'A0', state: 'done' },
] as const;
type LevelState = (typeof LEVELS)[number]['state'];
const MINI_COLORS = [C.red, C.pink, C.red2, C.pale, C.red, C.pale, C.pink, C.red2];

function MiniPoster({ i, state }: { i: number; state: LevelState }) {
  const filled = state === 'done' || (state === 'current' && i < 4);
  const bg = filled ? MINI_COLORS[i % MINI_COLORS.length] : 'rgba(255,248,245,0.4)';
  const dark = bg === C.red || bg === C.red2;
  return (
    <div
      style={{
        width: 150,
        height: 205,
        background: bg,
        border: filled ? 'none' : '3px solid rgba(26,16,24,0.15)',
        transform: `rotate(${((i % 3) - 1) * 1.8}deg)`,
        boxShadow: filled ? '0 6px 18px rgba(26,16,24,0.2)' : 'none',
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {filled ? (
        <React.Fragment>
          <div>
            <div
              style={{
                width: '85%',
                height: 12,
                background: dark ? 'rgba(255,248,245,0.8)' : 'rgba(26,16,24,0.55)',
                marginBottom: 8,
              }}
            />
            <div
              style={{
                width: '60%',
                height: 12,
                background: dark ? 'rgba(255,248,245,0.8)' : 'rgba(26,16,24,0.55)',
              }}
            />
          </div>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: dark ? C.cream : C.red,
              alignSelf: 'flex-end',
            }}
          />
        </React.Fragment>
      ) : state === 'next' && i === 0 ? (
        <div
          style={{
            margin: 'auto',
            fontFamily: OSW,
            fontWeight: 700,
            fontSize: 44,
            color: 'rgba(26,16,24,0.35)',
          }}
        >
          ?
        </div>
      ) : null}
    </div>
  );
}

function Zoomout({ t, dur, portrait }: SceneProps) {
  const zp = eio(seg(t, 0.1, dur * 0.62));
  const drift = seg(t, dur * 0.62, dur);
  const s = lerp(2.1, 0.33, zp) * lerp(1, 0.985, drift);
  const A = { x: 960, y: 1745 }; // focus start: current level stretch
  const B = { x: 960, y: 1560 }; // end focus: all 7 rows clear of the caption
  const fx = lerp(A.x, B.x, zp);
  const fy = lerp(A.y, B.y, zp);
  const tx = 960 - fx * s;
  const ty = 540 - fy * s;
  const capP = eo(seg(t, dur * 0.68, dur * 0.68 + 0.6));
  return (
    <div style={{ position: 'absolute', inset: 0, background: '#EFE3DA', overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 1920,
          height: 2400,
          transform: `translate(${tx}px, ${ty}px) scale(${s})`,
          transformOrigin: '0 0',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, background: '#EFE3DA' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'repeating-linear-gradient(0deg, rgba(26,16,24,0.07) 0 3px, transparent 3px 72px)',
            }}
          />
          {LEVELS.map((lv, li) => {
            const y = li * 340 + 30;
            const washed = lv.state === 'future' || lv.state === 'next';
            return (
              <div
                key={lv.name}
                style={{ position: 'absolute', left: 0, top: y, width: 1920, height: 320 }}
              >
                {washed && (
                  <div
                    style={{ position: 'absolute', inset: 0, background: 'rgba(255,248,245,0.62)' }}
                  />
                )}
                <div
                  style={{
                    position: 'absolute',
                    left: 60,
                    top: 46,
                    fontFamily: OSW,
                    fontWeight: 700,
                    fontSize: 120,
                    color: washed ? 'rgba(26,16,24,0.15)' : C.red,
                    opacity: washed ? 1 : 0.85,
                  }}
                >
                  {lv.name}
                </div>
                <div style={{ position: 'absolute', left: 340, top: 46, display: 'flex', gap: 34 }}>
                  {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                    <MiniPoster key={i} i={i} state={lv.state} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          paddingBottom: 44,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            opacity: capP,
            transform: `translateY(${lerp(24, 0, capP)}px)`,
            background: C.ink,
            // portrait-only: tighter card so the centered caption fits the
            // ~720px-wide crop window instead of being cut at both sides.
            padding: portrait ? '24px 32px' : '28px 52px',
            maxWidth: portrait ? 620 : 1500,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontFamily: OSW,
              fontWeight: 700,
              fontSize: 48,
              lineHeight: 1.08,
              textTransform: 'uppercase',
              color: C.cream,
              textWrap: 'balance',
            }}
          >
            Cada cartaz, uma mulher. Cada selo, uma qualidade que agora é sua.
          </div>
          {SHOW_ENGLISH && (
            <div
              style={{
                fontFamily: SERIF,
                fontStyle: 'italic',
                fontSize: 28,
                marginTop: 14,
                color: C.pale,
              }}
            >
              Every poster a woman. Every seal a quality that is now yours.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Timeline (prototype: window.OM_SCENES) ---------- */
const STAGE_W = 1920;
const STAGE_H = 1080;

/* ---------- Portrait camera (mobile-only; not in the prototype) ----------
 * Landscape-ish containers contain-fit the whole 1920x1080 stage, unchanged.
 * Portrait containers would letterbox that into an unreadable strip, so they
 * crop the stage horizontally instead: each scene declares where its key
 * content sits via portraitCam(t) -> { x, w } — x is the stage-space focus
 * center, w the stage width that must stay visible. The player scales so w
 * fits the container width (never larger than a full-height fit) and pans
 * the crop window to x, clamped to the stage edges. Scenes whose content
 * splits into two horizontal groups (Teaser, Classes, Ceremony) ease the
 * focus from one group to the other mid-scene, like a documentary pan. */
type PortraitCam = { x: number; w: number };
const camAt = (a: PortraitCam, b: PortraitCam, p: number): PortraitCam => ({
  x: lerp(a.x, b.x, p),
  w: lerp(a.w, b.w, p),
});

const SCENES: {
  name: string;
  dur: number;
  Comp: React.ComponentType<SceneProps>;
  pauseAfter?: boolean;
  portraitCam?: (t: number, dur: number) => PortraitCam;
}[] = [
  {
    name: 'Montagem',
    dur: 7,
    Comp: Montage,
    pauseAfter: true,
    // Full-height crop over the photo pile, then pull back onto the caption
    // column (portrait Montage narrows it to x 60..680) as it fades in.
    portraitCam: (t) => camAt({ x: 960, w: 520 }, { x: 380, w: 700 }, eio(seg(t, 3.6, 4.5))),
  },
  { name: 'O mural', dur: 4, Comp: WallScene, portraitCam: () => ({ x: 960, w: 700 }) },
  {
    name: 'Quem sera',
    dur: 4,
    Comp: Teaser,
    // Cartaz first (x 170..630), then pan right to the caption (x 780..1440).
    portraitCam: (t) => camAt({ x: 400, w: 720 }, { x: 1110, w: 720 }, eio(seg(t, 1.35, 2.2))),
  },
  {
    name: 'Aulas e EP',
    dur: 5,
    Comp: Classes,
    pauseAfter: true,
    // Glimpse the cartaz + progress ticks, then settle on caption + rows
    // (x 720..1500; portrait Classes narrows the caption to match).
    portraitCam: (t) => camAt({ x: 340, w: 660 }, { x: 1110, w: 860 }, eio(seg(t, 0.5, 1.3))),
  },
  {
    name: 'Cerimonia',
    dur: 6,
    Comp: Ceremony,
    pauseAfter: true,
    // Hold on the cartaz through the selo stamp, then pan to the text column
    // (x 900..1800) as its lines land.
    portraitCam: (t) => camAt({ x: 500, w: 780 }, { x: 1340, w: 940 }, eio(seg(t, 2.55, 3.4))),
  },
  { name: 'Zoom out', dur: 5, Comp: Zoomout, portraitCam: () => ({ x: 960, w: 720 }) },
];
const TOTAL_DUR = SCENES.reduce((sum, s) => sum + s.dur, 0); // 31s

// Boundaries the player stops at: the end of each `pauseAfter` scene (frozen
// last frame, click to continue) plus the end of the timeline (completes).
const GATES = (() => {
  const gates: number[] = [];
  let acc = 0;
  SCENES.forEach((s, i) => {
    acc += s.dur;
    if (s.pauseAfter || i === SCENES.length - 1) gates.push(acc);
  });
  return gates;
})();

/** elapsed seconds → active scene index + local time within that scene. */
function sceneAt(elapsed: number): { index: number; t: number } {
  let rest = elapsed;
  for (let i = 0; i < SCENES.length; i++) {
    if (rest < SCENES[i].dur || i === SCENES.length - 1) {
      return { index: i, t: Math.min(rest, SCENES[i].dur) };
    }
    rest -= SCENES[i].dur;
  }
  return { index: SCENES.length - 1, t: SCENES[SCENES.length - 1].dur };
}

/* ---------- Pause prompt (shown while paused between scenes) ---------- */
function PromptScrap({
  pt,
  en,
  rot,
  nudge = false,
}: {
  pt: string;
  en: string;
  rot: number;
  nudge?: boolean;
}) {
  return (
    <span
      style={{
        ['--rot' as string]: `${rot}deg`,
        position: 'relative',
        display: 'inline-block',
        background: C.cream,
        padding: '30px 48px 26px',
        boxShadow: '0 14px 40px rgba(26,16,24,0.35)',
        clipPath: 'polygon(1% 8%, 99% 0, 100% 88%, 97% 100%, 2% 96%, 0 82%)',
        animation: `muralPromptIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both${
          nudge ? ', muralPromptNudge 2.4s ease-in-out 1.4s infinite' : ''
        }`,
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: '34%',
          top: -14,
          width: 110,
          height: 30,
          transform: 'rotate(-4deg)',
          background: 'rgba(248,215,221,0.88)',
          boxShadow: '0 1px 4px rgba(26,16,24,0.25)',
        }}
      />
      <span
        style={{
          display: 'block',
          fontFamily: OSW,
          fontWeight: 700,
          fontSize: 36,
          lineHeight: 1,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: C.red,
        }}
      >
        {pt}
      </span>
      {SHOW_ENGLISH && (
        <span
          style={{
            display: 'block',
            marginTop: 10,
            fontFamily: SERIF,
            fontStyle: 'italic',
            fontSize: 22,
            color: C.ink,
            opacity: 0.65,
          }}
        >
          {en}
        </span>
      )}
    </span>
  );
}

// Portrait-only: the visible stage rect (stage px) plus a scrap upscale
// factor k, so the pause scraps anchor inside the crop window (whose corners
// differ from the stage corners) and stay comfortably tappable.
type PromptView = { x: number; y: number; w: number; h: number; k: number };

function PausePrompt({
  onContinue,
  onReplay,
  view,
}: {
  onContinue: () => void;
  onReplay: () => void;
  view?: PromptView;
}) {
  // Scrap anchors: stage corners on desktop (prototype values); crop-window
  // corners, slightly tighter and scaled by k, in portrait.
  const scrapPos = (corner: 'left' | 'right'): React.CSSProperties =>
    view
      ? {
          position: 'absolute',
          bottom: STAGE_H - (view.y + view.h) + 40,
          transform: `scale(${view.k})`,
          ...(corner === 'right'
            ? { right: STAGE_W - (view.x + view.w) + 24, transformOrigin: 'bottom right' }
            : { left: view.x + 24, transformOrigin: 'bottom left' }),
        }
      : corner === 'right'
        ? { position: 'absolute', right: 90, bottom: 64 }
        : { position: 'absolute', left: 90, bottom: 64 };
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 10 }}>
      <style>{`
        @keyframes muralPromptIn {
          from { opacity: 0; transform: rotate(var(--rot, -2deg)) translateY(26px); }
          to { opacity: 1; transform: rotate(var(--rot, -2deg)) translateY(0); }
        }
        @keyframes muralPromptNudge {
          0%, 100% { transform: rotate(var(--rot, -2deg)) translateY(0); }
          50% { transform: rotate(var(--rot, -2deg)) translateY(-7px); }
        }
      `}</style>
      {/* Clicking anywhere on the stage continues; the scrap is the affordance. */}
      <button
        type="button"
        onClick={onContinue}
        aria-label="Continuar"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          textAlign: 'right',
        }}
      >
        <span style={scrapPos('right')}>
          <PromptScrap pt="Continuar ✦" en="Click to continue" rot={-2} nudge />
        </span>
      </button>
      <button
        type="button"
        onClick={onReplay}
        aria-label="Rever"
        style={{
          ...scrapPos('left'),
          background: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <PromptScrap pt="↺ Rever" en="Replay" rot={2} />
      </button>
    </div>
  );
}

/* ---------- Player (replaces the prototype's Stage/SceneStage engine) ---------- */
export function MuralOnboardingAnimation({ onComplete, onProgress }: AnnouncementAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const resumeRef = useRef<() => void>(() => {});
  const replayRef = useRef<() => void>(() => {});

  // Latches so onComplete never double-fires (incl. StrictMode double-mount)
  // and callbacks stay fresh without restarting the clock.
  const completedRef = useRef(false);
  const lastReportedSecondRef = useRef(-1);
  const onCompleteRef = useRef(onComplete);
  const onProgressRef = useRef(onProgress);
  onCompleteRef.current = onComplete;
  onProgressRef.current = onProgress;

  const [reducedMotion] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  // Measure the container; the render below derives the stage fit from it
  // (contain fit in landscape, portrait crop camera otherwise).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () =>
      setBox((prev) =>
        prev.w === el.clientWidth && prev.h === el.clientHeight
          ? prev
          : { w: el.clientWidth, h: el.clientHeight },
      );
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const complete = () => {
      if (!completedRef.current) {
        completedRef.current = true;
        onCompleteRef.current();
      }
    };

    if (reducedMotion) {
      // Static final frame; complete after React settles.
      setElapsed(TOTAL_DUR);
      const id = window.setTimeout(complete, 300);
      return () => window.clearTimeout(id);
    }

    let rafId = 0;
    let running = false;
    let waiting = false; // paused at a scene boundary, awaiting a click
    let acc = 0; // elapsed playback seconds (excludes hidden-tab time)
    let lastTs: number | null = null;
    let nextGate = 0; // index into GATES of the boundary we play toward

    const report = (clamped: number) => {
      const second = Math.floor(clamped);
      if (second !== lastReportedSecondRef.current) {
        lastReportedSecondRef.current = second;
        onProgressRef.current?.(second);
      }
    };

    const step = (ts: number) => {
      if (lastTs !== null) acc += (ts - lastTs) / 1000;
      lastTs = ts;
      const gate = GATES[nextGate];
      if (acc >= gate) {
        acc = gate;
        running = false;
        report(gate);
        if (nextGate === GATES.length - 1) {
          // Play once: freeze on the final frame.
          setElapsed(TOTAL_DUR);
          complete();
          return;
        }
        // Hold the finished scene's last frame (epsilon keeps sceneAt from
        // rolling over to the next scene) and wait for a click.
        setElapsed(gate - 1e-4);
        waiting = true;
        setPaused(true);
        return;
      }
      setElapsed(acc);
      report(acc);
      rafId = requestAnimationFrame(step);
    };

    const start = () => {
      if (running || waiting || completedRef.current) return;
      running = true;
      lastTs = null;
      rafId = requestAnimationFrame(step);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(rafId);
    };

    resumeRef.current = () => {
      if (!waiting || completedRef.current) return;
      waiting = false;
      setPaused(false);
      nextGate += 1;
      if (!document.hidden) start();
    };

    // Rewind to the start of the section that just finished (the previous
    // gate) and play it again toward the same gate.
    replayRef.current = () => {
      if (!waiting || completedRef.current) return;
      waiting = false;
      setPaused(false);
      acc = nextGate === 0 ? 0 : GATES[nextGate - 1];
      setElapsed(acc);
      if (!document.hidden) start();
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) start();
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reducedMotion]);

  const { index, t } = sceneAt(elapsed);
  const scene = SCENES[index];
  const SceneComp = scene.Comp;

  // Landscape-ish containers keep the original centered contain fit
  // (pixel-identical to before). Portrait containers use the per-scene
  // portrait camera: scale so the scene's focus width fits the container
  // (capped at a full-height fit) and pan the crop window to the focus x.
  const portrait = box.w > 0 && box.h > 0 && box.w / box.h < 0.9;
  let stageStyle: React.CSSProperties;
  let promptView: PromptView | undefined;
  if (portrait) {
    const cam = scene.portraitCam ? scene.portraitCam(t, scene.dur) : { x: 960, w: 720 };
    const s = Math.max(0.05, Math.min(box.h / STAGE_H, box.w / cam.w));
    const viewW = Math.min(STAGE_W, box.w / s); // visible stage rect, stage px
    const viewH = Math.min(STAGE_H, box.h / s);
    const viewX = clamp(cam.x - viewW / 2, 0, STAGE_W - viewW);
    const viewY = (STAGE_H - viewH) / 2;
    stageStyle = {
      position: 'absolute',
      left: 0,
      top: 0,
      width: STAGE_W,
      height: STAGE_H,
      // Align the crop window with the container (centered on any axis where
      // the scaled stage is smaller than the container — ink bars, like film).
      transform: `translate(${(box.w - viewW * s) / 2 - viewX * s}px, ${(box.h - viewH * s) / 2 - viewY * s}px) scale(${s})`,
      transformOrigin: '0 0',
      background: C.ink,
      overflow: 'hidden',
    };
    promptView = {
      x: viewX,
      y: viewY,
      w: viewW,
      h: viewH,
      // Upscale the pause scraps on small screens so they stay tappable,
      // capped so both still fit side by side inside the crop window.
      k: Math.max(0.5, Math.min((viewW - 100) / 600, Math.max(1, 0.55 / s))),
    };
  } else {
    const scale =
      box.w > 0 ? Math.max(0.05, Math.min(box.w / STAGE_W, box.h / STAGE_H)) : 1;
    stageStyle = {
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: STAGE_W,
      height: STAGE_H,
      transform: `translate(-50%, -50%) scale(${scale})`,
      transformOrigin: 'center',
      background: C.ink,
      overflow: 'hidden',
    };
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: C.ink,
        overflow: 'hidden',
      }}
    >
      <div style={stageStyle}>
        <SceneComp t={t} dur={scene.dur} portrait={portrait} />
        {paused && (
          <PausePrompt
            onContinue={() => resumeRef.current()}
            onReplay={() => replayRef.current()}
            view={promptView}
          />
        )}
      </div>
    </div>
  );
}
